"use server";
import {defaultSession, SessionData, sessionOptions} from "@/lib/session";
import {signInSchema, TSignInSchema} from "@/schemas/auth";
import {User} from "@/types";
import {getIronSession} from "iron-session";
import {revalidatePath, revalidateTag} from "next/cache";
import {cookies} from "next/headers";
import {getLocale} from "next-intl/server";
import {fetchUserMe, fetchMembership, UserMeError, type UserMeResponse} from "@/server/auth/cached-fetchers";

/**
 * Bust all agency-specific Next.js fetch caches.
 * Call after login, logout, agency switch, or agency creation
 * so that every page renders with the correct agency's data.
 */
const revalidateAllAgencyCaches = (userId: number, oldAgencyId?: number, newAgencyId?: number) => {
    revalidateTag(`user-${userId}`, "max");
    revalidateTag(`membership-${userId}`, "max");
    revalidateTag(`email-config-${userId}`, "max");
    revalidateTag(`emails-INBOUND`, "max");
    revalidateTag(`emails-OUTBOUND`, "max");
    if (oldAgencyId) revalidateTag(`email-templates-${oldAgencyId}`, "max");
    if (newAgencyId) revalidateTag(`email-templates-${newAgencyId}`, "max");
    if (oldAgencyId) revalidateTag(`display-names-${oldAgencyId}`, "max");
    if (newAgencyId) revalidateTag(`display-names-${newAgencyId}`, "max");
    revalidateTag(`my-profile`, "max");
    revalidateTag(`agency-settings`, "max");
    revalidatePath("/");
};

// Function to retrieve the current session. If the session does not exist, it initializes it with default values.
export const getSession = async () => {
    const session = await getIronSession<SessionData>(
        await cookies(),
        sessionOptions,
    );
    // Assign default session values if they are missing
    if (!session.user) session.user = defaultSession.user;
    if (!session.token) session.token = defaultSession.token;
    if (!session.isLoggedIn) session.isLoggedIn = defaultSession.isLoggedIn;

    return session;
};

// Function to handle user sign-in
export const signIn = async ({
                                 username,
                                 password,
                                 rememberMe,
                             }: TSignInSchema) => {
    // Validate username and password against the defined schema
    const validatedFields = await signInSchema.safeParseAsync({
        username,
        password,
        rememberMe,
    });

    if (!validatedFields.success) {
        console.warn("Validation failed:", validatedFields.error); // Log validation errors
        return {error: "Invalid credentials!"};
    }

    // Attempt to sign in the user
    try {
        const pendingUser = {identifier: username, password};

        // console.log("Attempting to sign in with:", pendingUser);

        // Make a POST request to the authentication API
        const response = await fetch(`${process.env.RR_API_URL}/api/auth/local`, {
            method: "POST",
            headers: {
                accept: "application/json, text/plain, */*",
                "content-type": "application/json;charset=UTF-8",
            },
            body: JSON.stringify(pendingUser),
        });

        // console.log("Fetch status:", response.status); // Log the status of the response

        if (!response.ok) {
            const errorResponse = await response.json().catch(() => null);

            // 2FA-enabled account: the password was valid but the backend
            // withheld the JWT (Requires2FA gate on /auth/local). Trigger the
            // email OTP and let the form switch to the code step — the session
            // is minted later via verify2FAAndSignIn → exchangeAuthCode.
            if (
                response.status === 403 &&
                errorResponse?.error?.message === "requires2FA" &&
                typeof errorResponse?.email === "string"
            ) {
                const sent = await request2FACode(errorResponse.email, password);
                if (!sent.ok) {
                    return {
                        error: sent.error || "Could not send verification code!",
                        status: 500,
                    };
                }
                return {requires2FA: true as const, email: errorResponse.email, status: 403};
            }

            // If the response is not OK, log and return an error
            console.error("Sign in error response:", errorResponse);
            return {
                error: errorResponse?.message || "Invalid login credentials!",
                status: response.status,
            };
        }

        const signInResponse = await response.json();
        // console.log("Sign in response:", signInResponse); // Log the response for debugging

        // Check for a valid JWT in the response
        if (!signInResponse.jwt) {
            return {error: "Invalid login credentials!", status: 401};
        }

        // Fetch the user's data using the JWT token
        const userDataResponse = await fetch(
            `${process.env.RR_API_URL}/api/users/me?populate=role,img,sip,email_settings,email_settings_outbound,ringtone_settings,agency.logo,agency.owner`,
            {
                method: "GET",
                headers: {
                    accept: "application/json, text/plain, */*",
                    "content-type": "application/json;charset=UTF-8",
                    Authorization: `Bearer ${signInResponse.jwt}`,
                },
            },
        );

        if (!userDataResponse.ok) {
            const errorResponse = await userDataResponse.json();
            console.error("User data fetch error:", errorResponse);
            return {
                error: "Could not retrieve user data!",
                status: userDataResponse.status,
            };
        }

        const userData = await userDataResponse.json();

        // Retrieve the current session and update it with user data
        const session = await getSession();
        session.user = {
            id: userData.id,
            username: userData.username,
            email: userData.email,
            role: userData.role?.type || "authenticated",
            img: userData.img?.url || "",
            confirmed: userData.confirmed,
            phoneNumber: userData.phonesetup?.phone || "",
            blocked: userData.blocked,
            // Include agency data for data isolation
            agency: userData.agency ? {
                id: userData.agency.id,
                name: userData.agency.name || "",
                short: userData.agency.short,
                agency_type: userData.agency.agency_type,
                logo: userData.agency.logo?.url || "",
                onboarding_completed: userData.agency.onboarding_completed ?? false,
                context_timezone: userData.agency.context_timezone || undefined,
                use_browser_timezone: userData.agency.use_browser_timezone ?? undefined,
                cx_contracts_enabled: userData.agency.cx_contracts_enabled ?? false,
                cx_project_orientation: userData.agency.cx_project_orientation ?? "appointments",
                is_training: userData.agency.is_training ?? false,
            } : undefined,
            agent_color: userData.agent_color,
        };
        session.token = signInResponse.jwt;
        session.isLoggedIn = true;

        // Fetch agency membership + permissions
        try {
            const membershipRes = await fetch(
                `${process.env.RR_API_URL}/api/agency-memberships/my-membership`,
                { headers: { Authorization: `Bearer ${signInResponse.jwt}` } }
            );
            if (membershipRes.ok) {
                const membershipData = await membershipRes.json();
                if (membershipData.data?.role) {
                    session.user.agencyRole = membershipData.data.role.slug;
                }
                session.user.agencyDisplayName = membershipData.data?.display_name || null;
                // Phonesetup lives on membership
                if (membershipData.data?.phonesetup?.phone) {
                    session.user.phoneNumber = membershipData.data.phonesetup.phone;
                }
            }
        } catch (e) {
            console.error('Failed to fetch membership:', e);
        }

        // Save the session and redirect to the home page
        await session.save();

        // Bust stale caches so the first navigation after login is a clean partial render
        revalidateAllAgencyCaches(session.user.id, undefined, session.user.agency?.id);

        return {
            success: "User signed in successfully!",
            status: 200,
            token: session.token,
        };
    } catch (error) {
        console.error("Sign in error:", error); // Log the error for debugging
        return {error: "Something went wrong! Try again later!", status: 500};
    }
};

// Function to handle user sign-out
export const signOut = async () => {
        try {
            const session = await getSession();
            const locale = await getLocale()
            const userId = session.user?.id;
            const agencyId = session.user?.agency?.id;
            await session.destroy();
            if (userId) revalidateAllAgencyCaches(userId, agencyId);
            return {
                status: 200,
                ok: true,
                redirectUrl: `/${locale}/sign-in`,
            };
        } catch
            (error) {
            console.log(error);
            return {
                status: 500,
                ok: false,
                statusText: "Internal server error!",
                message: "Something went wrong, please try again later!",
            };
        }
    }
;

export const getCurrentUser = async () => {
    try {
        const session = await getSession();
        if (!session.user || !session.isLoggedIn || !session.token) {
            return {user: null, error: "Unauthorized!", status: 401, ok: false};
        }

        // Both fetches use unstable_cache (90s) keyed by userId — shared with
        // getAgencyPermissions / getAgencyMembership so multiple callers in the
        // same render only hit Strapi once.
        let userData: UserMeResponse;
        try {
            userData = await fetchUserMe(session.user.id, session.token);
        } catch (e) {
            const status = (e as UserMeError)?.status ?? 500;
            if (status === 502) {
                console.error("API returned Bad Gateway error (502)");
                return { user: null, ok: false, error: "API server is temporarily unavailable", status: 502 };
            }
            if (status === 401 || status === 403) {
                await session.destroy();
                return { user: null, ok: false, error: "Session expired or invalid token", status: 401 };
            }
            console.error("User data fetch error:", e);
            return { user: null, ok: false, error: "Could not fetch the user data!", status };
        }

        // The frontend treats agency as required on the User object — every
        // logged-in user has a primary agency. If users/me ever returns null,
        // surface it as an error rather than papering over with undefined and
        // crashing downstream consumers like sidebar/team-switcher.
        if (!userData.agency) {
            return {
                user: null,
                ok: false,
                error: "User has no agency assigned",
                status: 500,
            };
        }

        const membership = await fetchMembership(session.user.id, session.token);
        const agencyRole = membership.role?.slug;
        const agencyPermissions = membership.role?.permissions;
        const agencyDisplayName = membership.display_name;
        const membershipPhonesetup = membership.phonesetup ?? undefined;

        const currentUser: User = {
            id: userData.id,
            username: userData.username,
            firstName: userData.firstName || null,
            lastName: userData.lastName || null,
            email: userData.email,
            provider: userData.provider,
            confirmed: userData.confirmed,
            blocked: userData.blocked,
            calendar_token: userData.calendar_token,
            calendar_name: userData.calendar_name,
            createdAt: userData.createdAt,
            updatedAt: userData.updatedAt,
            prestige: userData.prestige,
            role: userData.role?.type || "authenticated",
            img: userData.img?.url || "",
            sip: userData.sip,
            email_settings: userData.email_settings,
            email_settings_outbound: userData.email_settings_outbound,
            ringtone_settings: userData.ringtone_settings,
            phonesetup: membershipPhonesetup ?? userData.phonesetup,
            agency: {
                ...userData.agency,
                logo: userData.agency.logo?.url || "",
                email_settings_outbound: userData.agency.email_settings_outbound,
            },
            token: session.token,
            rc_settings: userData.rc_settings,
            agent_color: userData.agent_color,
            // Agency-level permission system
            agencyRole,
            agencyPermissions,
            agencyDisplayName,
        };

        return {
            data: currentUser,
            success: "User session created successfully!",
            status: 200,
            ok: true,
        };
    } catch (error) {
        console.log(error);
        return {
            status: 500,
            ok: false,
            statusText: "Internal server error!",
            message: "Something went wrong, plese try again later!",
        };
    }
};

/**
 * Exchange a one-time auth code (from SaaS landing page) for a session.
 * Called by the /auth/callback page after the landing page redirects back with ?code=xxx.
 */
export const exchangeAuthCode = async (code: string) => {
    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/saas-auth/exchange-code`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code }),
            }
        );

        if (!response.ok) {
            return { ok: false, error: "Invalid or expired auth code" };
        }

        const data = await response.json();
        if (!data.jwt || !data.user) {
            return { ok: false, error: "Invalid auth code response" };
        }

        const { jwt, user: userData } = data;

        // Fetch agency membership (role slug + display name only — permissions
        // are too big for the cookie, fetched on-demand via getAgencyPermissions)
        let agencyRole: string | undefined;
        let agencyDisplayName: string | null = null;
        let membershipPhoneNumber: string | undefined;
        try {
            const membershipRes = await fetch(
                `${process.env.RR_API_URL}/api/agency-memberships/my-membership`,
                { headers: { Authorization: `Bearer ${jwt}` } }
            );
            if (membershipRes.ok) {
                const membershipData = await membershipRes.json();
                if (membershipData.data?.role) {
                    agencyRole = membershipData.data.role.slug;
                }
                agencyDisplayName = membershipData.data?.display_name || null;
                if (membershipData.data?.phonesetup?.phone) {
                    membershipPhoneNumber = membershipData.data.phonesetup.phone;
                }
            }
        } catch (e) {
            console.error("[exchangeAuthCode] Failed to fetch membership:", e);
        }

        const session = await getSession();
        session.user = {
            id: userData.id,
            username: userData.username,
            email: userData.email,
            role: userData.role?.type || "authenticated",
            img: userData.img?.url || "",
            confirmed: userData.confirmed,
            phoneNumber: membershipPhoneNumber || userData.phonesetup?.phone || "",
            blocked: userData.blocked,
            agency: userData.agency ? {
                id: userData.agency.id,
                name: userData.agency.name || "",
                short: userData.agency.short || "",
                agency_type: userData.agency.agency_type || "",
                logo: userData.agency.logo?.url || "",
                onboarding_completed: userData.agency.onboarding_completed ?? false,
                cx_contracts_enabled: userData.agency.cx_contracts_enabled ?? false,
                cx_project_orientation: userData.agency.cx_project_orientation ?? "appointments",
                is_training: userData.agency.is_training ?? false,
            } : undefined,
            agent_color: userData.agent_color,
            agencyRole,
            agencyDisplayName,
        };
        session.token = jwt;
        session.isLoggedIn = true;
        await session.save();

        return { ok: true };
    } catch (error) {
        console.error("[exchangeAuthCode] Error:", error);
        return { ok: false, error: "Authentication failed" };
    }
};

/**
 * Step 1 of the CRM 2FA sign-in: ask saas-auth to email the 6-digit code.
 * saas-auth re-validates the password itself, so this action adds no bypass.
 */
export const request2FACode = async (email: string, password: string) => {
    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/saas-auth/send-2fa-code`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            }
        );
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
            return {
                ok: false as const,
                error: data?.error?.message || "Could not send verification code",
            };
        }
        if (!data.requires2FA) {
            // 2FA was disabled between attempts — have the user just sign in again.
            return { ok: false as const, error: "2FA no longer required — please sign in again" };
        }
        return { ok: true as const };
    } catch (error) {
        console.error("[request2FACode] Error:", error);
        return { ok: false as const, error: "Could not send verification code" };
    }
};

/**
 * Step 2 of the CRM 2FA sign-in: verify the OTP, then redeem the one-time
 * authCode it returns through the existing exchangeAuthCode() — same session
 * minting path as the landing-page SSO handoff, no new session code.
 */
export const verify2FAAndSignIn = async (email: string, code: string) => {
    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/saas-auth/verify-2fa-code`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, code }),
            }
        );
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success || !data?.authCode) {
            return {
                ok: false as const,
                error: data?.error?.message || "Invalid verification code",
            };
        }
        return exchangeAuthCode(data.authCode);
    } catch (error) {
        console.error("[verify2FAAndSignIn] Error:", error);
        return { ok: false as const, error: "Verification failed" };
    }
};

// Function to switch the user's active agency
export const switchAgency = async (agencyId: number) => {
    const session = await getSession();
    if (!session.isLoggedIn || !session.token || !session.user?.id) {
        return { error: "Not authorized", status: 401, ok: false };
    }

    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/agency-memberships/switch`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session.token}`,
                },
                body: JSON.stringify({ agencyId }),
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                error: errorData.error?.message || "Failed to switch agency",
                status: response.status,
                ok: false,
            };
        }

        const data = await response.json();

        // Remember old agency ID so we can bust its cached templates
        const oldAgencyId = session.user?.agency?.id;

        // Update session with new agency data
        if (data.agency) {
            session.user.agency = {
                id: data.agency.id,
                name: data.agency.name || "",
                short: data.agency.short || "",
                agency_type: data.agency.agency_type || "",
                logo: "", // Will be refreshed on next page load
                onboarding_completed: data.agency.onboarding_completed ?? false,
                context_timezone: data.agency.context_timezone || undefined,
                use_browser_timezone: data.agency.use_browser_timezone ?? undefined,
                cx_contracts_enabled: data.agency.cx_contracts_enabled ?? false,
                cx_project_orientation: data.agency.cx_project_orientation ?? "appointments",
                is_training: data.agency.is_training ?? false,
            };
        }
        if (data.role) {
            session.user.agencyRole = data.role.slug;
        }

        await session.save();

        revalidateAllAgencyCaches(session.user.id, oldAgencyId, agencyId);
        // The /mine micro-cache must not serve the pre-switch active agency
        const { bustMyMembershipsCache } = await import("@/server/data/auth-memberships");
        await bustMyMembershipsCache(session.user.id);

        return { ok: true, status: 200 };
    } catch (error) {
        console.error("[switchAgency] Error:", error);
        return { error: "Failed to switch agency", status: 500, ok: false };
    }
};

// Function to create a new agency (always CF type)
export const createAgency = async (data: { name: string; logoId?: number; accessCode: string; ownerDisplayName?: string; ownerPosition?: string }) => {
    const session = await getSession();
    if (!session.isLoggedIn || !session.token || !session.user?.id) {
        return { error: "Not authorized", status: 401, ok: false as const };
    }

    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/agencies/create`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session.token}`,
                },
                body: JSON.stringify({
                    name: data.name,
                    logoId: data.logoId,
                    accessCode: data.accessCode,
                    ownerDisplayName: data.ownerDisplayName,
                    ownerPosition: data.ownerPosition,
                }),
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                error: errorData.error?.message || "Failed to create agency",
                status: response.status,
                ok: false as const,
            };
        }

        const result = await response.json();

        // Remember old agency ID so we can bust its cached templates
        const oldAgencyId = session.user?.agency?.id;

        // Update session with new agency data
        if (result.agency) {
            session.user.agency = {
                id: result.agency.id,
                name: result.agency.name || "",
                short: result.agency.short || "",
                agency_type: result.agency.agency_type || "CF",
                logo: "",
                onboarding_completed: result.agency.onboarding_completed ?? false,
                context_timezone: undefined,
                use_browser_timezone: undefined,
                cx_contracts_enabled: result.agency.cx_contracts_enabled ?? false,
                cx_project_orientation: result.agency.cx_project_orientation ?? "appointments",
                is_training: result.agency.is_training ?? false,
            };
        }
        if (result.role) {
            session.user.agencyRole = result.role.slug;
        }

        // Refresh role slug + phonesetup for the new agency (permissions are
        // fetched on-demand server-side via getAgencyPermissions, not stored
        // in the cookie)
        try {
            const membershipRes = await fetch(
                `${process.env.RR_API_URL}/api/agency-memberships/my-membership`,
                { headers: { Authorization: `Bearer ${session.token}` } }
            );
            if (membershipRes.ok) {
                const membershipData = await membershipRes.json();
                if (membershipData.data?.role) {
                    session.user.agencyRole = membershipData.data.role.slug;
                }
                if (membershipData.data?.phonesetup?.phone) {
                    session.user.phoneNumber = membershipData.data.phonesetup.phone;
                }
            }
        } catch (e) {
            console.error('Failed to fetch membership after agency creation:', e);
        }

        await session.save();

        revalidateAllAgencyCaches(session.user.id, oldAgencyId, result.agency?.id);

        return { ok: true as const, status: 200 };
    } catch (error) {
        console.error("[createAgency] Error:", error);
        return { error: "Failed to create agency", status: 500, ok: false as const };
    }
};

/**
 * Refresh the session's cached agency role/display name by re-fetching
 * `/api/agency-memberships/my-membership`. Useful after agency-side role or
 * membership changes that should take effect without forcing the operator to
 * log out and log back in.
 *
 * Permissions are deliberately NEVER written to the iron-session cookie:
 * iron-session hard-throws above 4096 bytes and permission arrays can be
 * large. Fresh permissions always come from getAgencyPermissions() /
 * the membership fetch; this returns them to the caller without persisting.
 *
 * Returns `{ ok: true, permissions }` on success, `{ ok: false, error }` on
 * failure. Safe to call repeatedly — no-op if session has no token yet.
 */
export const refreshAgencyPermissions = async () => {
    try {
        const session = await getSession();
        if (!session?.isLoggedIn || !session?.token || !session?.user) {
            return { ok: false as const, error: "Not authenticated" };
        }
        const response = await fetch(
            `${process.env.RR_API_URL}/api/agency-memberships/my-membership`,
            { headers: { Authorization: `Bearer ${session.token}` } },
        );
        if (!response.ok) {
            return { ok: false as const, error: `backend ${response.status}` };
        }
        const data = await response.json();
        if (data?.data?.role) {
            session.user.agencyRole = data.data.role.slug;
        }
        if (data?.data?.display_name) {
            session.user.agencyDisplayName = data.data.display_name;
        }
        await session.save();
        return { ok: true as const, permissions: data?.data?.role?.permissions || [] };
    } catch (error) {
        console.error("[refreshAgencyPermissions] Error:", error);
        return { ok: false as const, error: "Failed to refresh permissions" };
    }
};

// Request password reset — always returns ok (anti-enumeration)
export const requestPasswordReset = async (email: string) => {
    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/saas-auth/forgot-password`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            },
        );
        if (!response.ok) {
            console.error("[requestPasswordReset] backend returned", response.status);
        }
    } catch (error) {
        console.error("[requestPasswordReset] Error:", error);
    }
    return { ok: true as const };
};

// Reset password using token from email link
export const resetPassword = async (token: string, password: string) => {
    try {
        const response = await fetch(
            `${process.env.RR_API_URL}/api/saas-auth/reset-password`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, password }),
            },
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                ok: false as const,
                error: errorData.error?.message || errorData.message || "Failed to reset password",
                status: response.status,
            };
        }
        return { ok: true as const, status: 200 };
    } catch (error) {
        console.error("[resetPassword] Error:", error);
        return { ok: false as const, error: "Failed to reset password", status: 500 };
    }
};
